import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Flex, Modal, Tooltip } from "antd";
import { useCallback, useRef } from "react";
import useSystemNoticeApi from "../apiHooks/useSystemNoticeApi";
import { useToggleState } from "../common/hooks/useToggleState";
import { useNotificationContext } from "../core/contexts/NotificationContextProvider";
import useAuthority from "../permissions/hooks/useAuthority";
import {
  MODULES,
  PERMISSION_FEATURES,
} from "../permissions/types/permission-types";
import SystemNoticeComponent from "./systemNotice.component";

const SystemNotice = () => {
  const [isSystemNoticeModalOpen, toggleSystemNoticeModal] =
    useToggleState(false);

  const { notify } = useNotificationContext();
  const systemNoticeTableRef = useRef();

  const systemNoticeModalRef = useRef();

  const refreshTable = useCallback(
    () => systemNoticeTableRef.current.refresh(),
    []
  );

  const {
    deleteSystemNoticeApi: { delete: deleteSystemNotice },
  } = useSystemNoticeApi();

  const auth = useAuthority(MODULES.NOTICE_MANAGEMENT);

  const handleSystemNoticeDeletion = useCallback(
    (confirm = false) =>
      async (data) => {
        if (!confirm) {
          Modal.confirm({
            maskClosable: true,
            centered: true,
            title: null,
            icon: null,
            width: 400,
            content: (
              <div style={{ textAlign: "center", padding: "16px 8px 8px" }}>
                {/* Red circle with SVG trash icon */}
                <div style={{
                  width: 60, height: 60, borderRadius: "50%",
                  background: "#fef2f2", border: "4px solid #fee2e2",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 20px",
                }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 11v6M14 11v6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <p style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1a1d2e", margin: "0 0 8px" }}>
                  Delete Notice?
                </p>
                <p style={{ fontSize: "0.875rem", color: "#6b7a99", margin: 0, lineHeight: 1.6 }}>
                  This notice will be permanently deleted and cannot be recovered.
                </p>
              </div>
            ),
            footer: (
              <div style={{ display: "flex", gap: 10, justifyContent: "center", padding: "8px 0 4px" }}>
                <button
                  onClick={() => Modal.destroyAll()}
                  onMouseEnter={e => { e.currentTarget.style.background = "#edf3fa"; e.currentTarget.style.borderColor = "#00A9E0"; e.currentTarget.style.color = "#1B2A6B"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#f5f9fd"; e.currentTarget.style.borderColor = "#dde8f4"; e.currentTarget.style.color = "#5a7a9a"; }}
                  style={{
                    flex: 1, height: 40, borderRadius: 8, border: "1px solid #dde8f4",
                    background: "#f5f9fd", color: "#5a7a9a", fontWeight: 600,
                    fontSize: "0.875rem", cursor: "pointer", transition: "all 0.18s ease",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => { Modal.destroyAll(); handleSystemNoticeDeletion(true)(data); }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#b91c1c"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#dc2626"; }}
                  style={{
                    flex: 1, height: 40, borderRadius: 8, border: "none",
                    background: "#dc2626", color: "#ffffff", fontWeight: 600,
                    fontSize: "0.875rem", cursor: "pointer", transition: "background 0.18s ease",
                  }}
                >
                  Delete
                </button>
              </div>
            ),
            onOk: () => handleSystemNoticeDeletion(true)(data),
          });
        } else {
          const res = await deleteSystemNotice(data.id);
          if (res) {
            notify({
              message: "Record deleted successfully",
            });
            refreshTable();
          }
        }
      },
    [deleteSystemNotice, notify, refreshTable]
  );

  const handleEdit = useCallback(
    (data) => {
      toggleSystemNoticeModal();
      systemNoticeModalRef.current.getSystemNoticeRecord(data);
    },
    [toggleSystemNoticeModal]
  );

  const actionCellRenderer = useCallback(
    (data) => {
      return (
        <Flex gap={8} align="center">
          <Tooltip title={auth[PERMISSION_FEATURES.EDIT] ? "Edit" : "View"} placement="top">
            <Button
              onClick={() => handleEdit(data)}
              size="middle"
              type="default"
              icon={auth[PERMISSION_FEATURES.EDIT] ? <EditOutlined /> : <EyeOutlined />}
              style={{ borderRadius: 8, width: 34, padding: 0, borderColor: auth[PERMISSION_FEATURES.EDIT] ? "#86efac" : "#93c5fd", color: auth[PERMISSION_FEATURES.EDIT] ? "#22c55e" : "#3b82f6", transition: "all 0.18s ease" }}
              onMouseEnter={e => { const c = auth[PERMISSION_FEATURES.EDIT] ? "#22c55e" : "#3b82f6"; e.currentTarget.style.background = c; e.currentTarget.style.borderColor = c; e.currentTarget.style.color = "#ffffff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = auth[PERMISSION_FEATURES.EDIT] ? "#86efac" : "#93c5fd"; e.currentTarget.style.color = auth[PERMISSION_FEATURES.EDIT] ? "#22c55e" : "#3b82f6"; }}
            />
          </Tooltip>
          {auth[PERMISSION_FEATURES.DELETE] && (
            <Tooltip title="Delete" placement="top">
              <Button
                onClick={() => handleSystemNoticeDeletion(false)(data)}
                size="middle"
                type="default"
                icon={<DeleteOutlined />}
                style={{ borderRadius: 8, width: 34, padding: 0, borderColor: "#fca5a5", color: "#ef4444", transition: "all 0.18s ease" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#ef4444"; e.currentTarget.style.borderColor = "#ef4444"; e.currentTarget.style.color = "#ffffff"; }}
                onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = "#fca5a5"; e.currentTarget.style.color = "#ef4444"; }}
              />
            </Tooltip>
          )}
        </Flex>
      );
    },
    [auth, handleEdit, handleSystemNoticeDeletion]
  );

  return (
    <SystemNoticeComponent
      actionCellRenderer={actionCellRenderer}
      isSystemNoticeModalOpen={isSystemNoticeModalOpen}
      toggleSystemNoticeModal={toggleSystemNoticeModal}
      refreshTable={refreshTable}
      systemNoticeTableRef={systemNoticeTableRef}
      systemNoticeModalRef={systemNoticeModalRef}
      auth={auth}
    />
  );
};

export default SystemNotice;
